/**
 * Animation configuration constants for GJL Hub cinematic experience
 * Creative Bold aesthetic: strong parallax, dramatic scale, experimental
 */

export const ANIMATION = {
  duration: {
    fast: 0.4,
    normal: 0.8,
    slow: 1.2,
    dramatic: 1.5,
    hero: 1.2,
    typing: 0.04, // Per character
  },
  ease: {
    reveal: 'power3.out',
    heroIn: 'power3.inOut',
    bounce: 'back.out(1.7)',
    elastic: 'elastic.out(1, 0.3)',
    smooth: 'power2.out',
    scrub: 'none',
  },
  distance: {
    short: 40,
    normal: 80,
    long: 120,
    dramatic: 160,
  },
  blur: {
    light: 4,
    normal: 8,
    heavy: 12,
    dramatic: 20,
  },
  stagger: {
    fast: 0.08,
    normal: 0.15,
    slow: 0.2,
    character: 0.04,
  },
  scale: {
    subtle: 0.97,
    normal: 0.92,
    dramatic: 0.85,
    oversized: 3, // For act numbers scaling in
  },
  parallax: {
    background: 0.1,
    midground: 0.3,
    foreground: 0.5,
  },
  magnetic: {
    maxTilt: 8,
    lightIntensity: 0.15,
    springDuration: 0.6,
    pullRadius: 100,
    maxDisplacement: 12,
  },
  velocity: {
    maxScaleY: 1.08,
    maxSkew: 2,
  },
  spring: {
    cardEase: 'elastic.out(1, 0.5)',
    delayVariance: 0.08,
  },
} as const

/**
 * Direction config for directional reveals
 */
export const DIRECTION_CONFIG = {
  top: { y: -ANIMATION.distance.normal, x: 0 },
  right: { x: ANIMATION.distance.normal, y: 0 },
  bottom: { y: ANIMATION.distance.normal, x: 0 },
  left: { x: -ANIMATION.distance.normal, y: 0 },
  fade: { x: 0, y: 0 },
} as const

export type Direction = keyof typeof DIRECTION_CONFIG

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Check if device is mobile
 */
export function isMobile(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(max-width: 768px)').matches
}

/**
 * Get parallax speed adjusted for mobile
 */
export function getParallaxSpeed(speed: number): number {
  return isMobile() ? speed * 0.5 : speed
}
